import { Icon } from '@iconify/react';

export type InvasionLeaderboardType = 'top-score' | 'top-assist' | 'top-mmr' | 'top-save';

export interface InvasionPlayerLeaderboardItem {
  id: string | number;
  name: string;
  avatar?: string;
  role?: string;
  team?: string;
  goals: number;
  assists: number;
  saves?: number;
  deltaMmr: number;
  matchesPlayed: number;
}

interface InvasionPlayerLeaderboardProps {
  type: InvasionLeaderboardType;
  players: InvasionPlayerLeaderboardItem[];
  emptyMessage?: string;
  onShare?: () => void;
  onDownload?: () => void;
  downloading?: boolean;
  showActions?: boolean;
}

const getInitials = (name?: string): string => {
  if (!name) return 'P';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0][0] && parts[1][0]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getRowBgClass = (rank: number): string => {
  switch (rank) {
    case 1:
      return 'bg-amber-50/20 hover:bg-amber-50/40';
    case 2:
      return 'bg-slate-50/30 hover:bg-slate-50/60';
    case 3:
      return 'bg-orange-50/20 hover:bg-orange-50/40';
    default:
      return 'hover:bg-gray-50/60';
  }
};

const getAvatarGradient = (rank: number): string => {
  switch (rank) {
    case 1:
      return 'bg-gradient-to-br from-amber-400 to-amber-600';
    case 2:
      return 'bg-gradient-to-br from-slate-400 to-slate-600';
    case 3:
      return 'bg-gradient-to-br from-orange-400 to-amber-700';
    default:
      return 'bg-gradient-to-br from-gray-400 to-gray-600';
  }
};

const renderRankBadge = (rank: number) => {
  switch (rank) {
    case 1:
      return (
        <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 font-black text-xs flex items-center justify-center shadow-2xs border border-amber-300">
          1
        </span>
      );
    case 2:
      return (
        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 font-black text-xs flex items-center justify-center shadow-2xs border border-slate-300">
          2
        </span>
      );
    case 3:
      return (
        <span className="w-6 h-6 rounded-full bg-orange-100 text-amber-800 font-black text-xs flex items-center justify-center shadow-2xs border border-orange-200">
          3
        </span>
      );
    default:
      return <span className="font-bold text-xs text-gray-400">{rank}</span>;
  }
};

const renderMmrDeltaBadge = (deltaMmr: number) => {
  if (deltaMmr > 0) {
    return (
      <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-emerald-50 border border-emerald-200 text-[#16A34A] font-extrabold text-xs sm:text-sm">
        <Icon icon="ph:trend-up-bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span>+{deltaMmr.toFixed(1)} MMR</span>
      </div>
    );
  }

  if (deltaMmr < 0) {
    return (
      <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-red-50 border border-red-200 text-[#DC2626] font-extrabold text-xs sm:text-sm">
        <Icon icon="ph:trend-down-bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span>{deltaMmr.toFixed(1)} MMR</span>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-gray-50 border border-gray-200 text-gray-500 font-bold text-xs sm:text-sm">
      <Icon icon="ph:minus-bold" className="w-3 h-3" />
      <span>0.0 MMR</span>
    </div>
  );
};

export default function InvasionPlayerLeaderboard({
  type,
  players,
  emptyMessage,
  onShare,
  onDownload,
  downloading = false,
  showActions = true,
}: InvasionPlayerLeaderboardProps) {
  const getHeaderInfo = () => {
    switch (type) {
      case 'top-score':
        return {
          title: 'Top Score Event',
          icon: 'ph:soccer-ball-bold',
          iconBg: 'bg-[#FFF5EE] text-[#FC480D]',
          emptyTitle: 'Belum Ada Gol Tercatat',
          emptyDesc:
            emptyMessage ||
            'Catatan gol akan muncul setelah skor pertandingan atau gol pada rekap dimasukkan.',
        };
      case 'top-assist':
        return {
          title: 'Top Assist Event',
          icon: 'ph:sneaker-move-bold',
          iconBg: 'bg-blue-50 text-blue-600',
          emptyTitle: 'Belum Ada Assist Tercatat',
          emptyDesc:
            emptyMessage ||
            'Catatan assist akan muncul saat gol dengan assist dicatat pada rekap pertandingan.',
        };
      case 'top-mmr':
        return {
          title: 'Top MMR Gain Event',
          icon: 'ph:trend-up-bold',
          iconBg: 'bg-emerald-50 text-emerald-600',
          emptyTitle: 'Belum Ada MMR Gain',
          emptyDesc:
            emptyMessage ||
            'Kenaikan MMR akan terhitung setelah host melakukan Push MMR pada rekap pertandingan.',
        };
      case 'top-save':
        return {
          title: 'Top Save Event',
          icon: 'ph:hand-palm-bold',
          iconBg: 'bg-purple-50 text-purple-600',
          emptyTitle: 'Belum Ada Save Tercatat',
          emptyDesc:
            emptyMessage ||
            'Catatan save akan muncul saat penyelamatan kiper dicatat pada rekap pertandingan.',
        };
    }
  };

  const info = getHeaderInfo();
  const displayPlayers = players.slice(0, 10);

  if (displayPlayers.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#E0E6ED] p-8 text-center shadow-2xs">
        <div
          className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${info.iconBg}`}
        >
          <Icon icon={info.icon} className="w-7 h-7" />
        </div>
        <h3 className="text-base font-bold text-[#0E1726] mb-1">{info.emptyTitle}</h3>
        <p className="text-xs text-gray-500 max-w-sm mx-auto leading-relaxed">{info.emptyDesc}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs overflow-hidden w-full">
      {/* Header Info */}
      <div className="p-4 sm:p-5 border-b border-[#F0F2F4] flex items-center justify-between gap-3 bg-[#FAFBFD]">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 shadow-2xs ${info.iconBg}`}
          >
            <Icon icon={info.icon} className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm sm:text-base font-bold text-[#0E1726] truncate">
              {info.title}
            </h3>
          </div>
        </div>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 shrink-0">
          {displayPlayers.length} Pemain
        </span>
      </div>

      {/* Players List */}
      <div className="divide-y divide-[#F0F2F4]">
        {displayPlayers.map((player, index) => {
          const rank = index + 1;
          const isRank1 = rank === 1;

          return (
            <div
              key={`${player.id}-${index}`}
              className={`p-3.5 sm:p-4 flex items-center justify-between gap-3 transition-colors ${getRowBgClass(rank)}`}
            >
              {/* Left: Rank & Avatar & Info */}
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* Rank Badge */}
                <div className="w-7 text-center shrink-0 flex items-center justify-center">
                  {renderRankBadge(rank)}
                </div>

                {/* Avatar */}
                <div className="shrink-0">
                  {player.avatar ? (
                    <img
                      src={player.avatar}
                      alt={player.name}
                      className="w-10 h-10 aspect-square rounded-2xl object-cover shrink-0 border border-gray-100 shadow-2xs"
                    />
                  ) : (
                    <div
                      className={`w-10 h-10 aspect-square rounded-2xl flex items-center justify-center text-xs font-black shadow-2xs text-white shrink-0 ${getAvatarGradient(rank)}`}
                    >
                      {getInitials(player.name)}
                    </div>
                  )}
                </div>

                {/* Player Name & Role / Team */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-xs sm:text-sm font-bold text-[#0E1726] truncate max-w-[180px] sm:max-w-none">
                      {player.name}
                    </p>
                    {isRank1 && (
                      <span title="Peringkat 1" className="inline-flex items-center shrink-0">
                        <Icon icon="ph:crown-fill" className="w-3.5 h-3.5 text-amber-500" />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {player.role && (
                      <span className="inline-flex items-center text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-[#FFF5EE] text-[#FC480D]">
                        {player.role}
                      </span>
                    )}
                    {player.team && (
                      <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 max-w-[120px] truncate">
                        {player.team}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Stat Metric Display */}
              {type === 'top-score' && (
                <div className="text-right shrink-0">
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-[#FFF5EE] border border-[#FFEDD5] text-[#FC480D] font-extrabold text-xs sm:text-sm">
                    <Icon icon="ph:soccer-ball-bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>{player.goals} Gol</span>
                  </div>
                  <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                    {player.assists} Assist · {player.matchesPlayed} Main
                  </p>
                </div>
              )}

              {type === 'top-assist' && (
                <div className="text-right shrink-0">
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-blue-50 border border-blue-200 text-blue-600 font-extrabold text-xs sm:text-sm">
                    <Icon icon="ph:sneaker-move-bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>{player.assists} Assist</span>
                  </div>
                  <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                    {player.goals} Gol · {player.matchesPlayed} Main
                  </p>
                </div>
              )}

              {type === 'top-mmr' && (
                <div className="text-right shrink-0">
                  {renderMmrDeltaBadge(player.deltaMmr)}
                  <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                    {player.matchesPlayed} Main · {player.goals}G {player.assists}A
                  </p>
                </div>
              )}

              {type === 'top-save' && (
                <div className="text-right shrink-0">
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-purple-50 border border-purple-200 text-purple-600 font-extrabold text-xs sm:text-sm">
                    <Icon icon="ph:hand-palm-bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>{player.saves || 0} Save</span>
                  </div>
                  <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                    {player.matchesPlayed} Main · {player.goals}G {player.assists}A
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom Action Buttons (Bagikan & Download Statistik) */}
      {showActions && (onShare || onDownload) && (
        <div className="p-3.5 sm:p-4 bg-white border-t border-[#F0F2F4] flex items-center gap-3">
          {onShare && (
            <button
              type="button"
              onClick={onShare}
              className="flex-1 h-11 sm:h-12 rounded-2xl bg-[#FFF5EE] text-[#FC480D] font-bold text-xs sm:text-sm flex items-center justify-center gap-2 border border-[#FFEDD5] hover:bg-[#FFEAE0] active:scale-98 transition-all cursor-pointer"
            >
              <Icon icon="ph:share-network" className="w-4 h-4 sm:w-5 sm:h-5 text-[#FC480D]" />
              <span>Bagikan</span>
            </button>
          )}
          {onDownload && (
            <button
              type="button"
              onClick={onDownload}
              disabled={downloading}
              className="flex-1 h-11 sm:h-12 rounded-2xl bg-[#FC480D] hover:bg-[#D83B08] text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 active:scale-98 transition-all shadow-sm cursor-pointer disabled:opacity-60"
            >
              {downloading ? (
                <>
                  <Icon icon="ph:spinner-bold" className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  <span>Mengunduh...</span>
                </>
              ) : (
                <>
                  <Icon icon="ph:download-simple-bold" className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Download Statistik</span>
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
